import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { MessageCircle, QrCode, Newspaper, UserRound, Gamepad2, Image as ImageIcon, Award, Mail, Sparkles, Lock, Unlock, Share2, Crown } from "lucide-react";
import { api } from "@/lib/api";
import { useGrowth, inviteMsg, loadGrowth } from "@/lib/growth";
import { openShare } from "@/lib/share";
import { whenFree } from "@/lib/popupQueue";
import { useAuth } from "@/context/AuthContext";

export const INVITE_WAYS = [
  { id: "link", icon: MessageCircle, title: "Dein persönlicher Link", text: "Per WhatsApp, Messenger, Telegram, X oder SMS verschicken", to: "/einladen" },
  { id: "qr", icon: QrCode, title: "QR-Code zum Scannen", text: "Freunde scannen ihn direkt von deinem Handy", to: "/einladen" },
  { id: "post", icon: Newspaper, title: "Beiträge teilen", text: "Jeder geteilte Beitrag ist gleichzeitig eine Einladung", to: "/feed" },
  { id: "profile", icon: UserRound, title: "Dein Profil teilen", text: "Zeig, wer du bist – mit schöner Vorschau", to: "/profil" },
  { id: "game", icon: Gamepad2, title: "Freunde herausfordern", text: "Spielergebnisse von Zwei Herzen & Herz-Läufer teilen", to: "/spiele" },
  { id: "story", icon: ImageIcon, title: "Werbebild für deine Story", text: "Für Instagram, TikTok, Facebook & WhatsApp-Status", to: "/einladen" },
  { id: "badge", icon: Award, title: "Abzeichen & Erfolge teilen", text: "Zeig deine Erfolge und nimm Freunde mit", to: "/profil" },
  { id: "mail", icon: Mail, title: "Per E-Mail einladen", text: "Ideal für Familie, Kollegen & ältere Freunde", to: "/einladen" },
];

export function InviteWays({ onPick, compact }) {
  return (
    <ul className={`grid ${compact ? "grid-cols-2 gap-2" : "sm:grid-cols-2 gap-3"}`} data-testid="invite-ways">
      {INVITE_WAYS.map((w) => (
        <li key={w.id}>
          <Link to={w.to} onClick={onPick} data-testid={`invite-way-${w.id}`} className={`h-full flex items-start gap-3 rounded-2xl border border-pink-100 bg-white hover:border-rose-300 hover:-translate-y-0.5 transition-transform ${compact ? "p-2.5" : "p-3.5"}`}>
            <span className={`${compact ? "w-8 h-8" : "w-10 h-10"} shrink-0 rounded-xl bg-rose-50 text-rose-500 flex items-center justify-center`}><w.icon className={compact ? "w-4 h-4" : "w-5 h-5"} /></span>
            <span className="min-w-0">
              <span className={`block font-bold text-slate-800 leading-tight ${compact ? "text-xs" : "text-sm"}`}>{w.title}</span>
              {!compact && <span className="block text-xs text-slate-500 mt-0.5">{w.text}</span>}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

export function InviteWaysSection() {
  return (
    <section className="bg-white rounded-3xl p-5 sm:p-6 border border-pink-100 shadow-sm space-y-4" data-testid="invite-ways-section">
      <div>
        <h3 className="font-bold text-lg flex items-center gap-2"><Sparkles className="w-5 h-5 text-rose-500" /> So viele Wege, Freunde einzuladen</h3>
        <p className="text-xs text-slate-500 mt-1">Egal wie du teilst – jede Anmeldung über deinen Link zählt als dein eingeladener Freund.</p>
      </div>
      <InviteWays />
    </section>
  );
}

function Sheet({ g, onClose }) {
  const navigate = useNavigate();
  const invite = () => { onClose(); openShare({ kind: "invite", title: "Freunde einladen", msg: inviteMsg }); };
  return createPortal(
    <div role="dialog" aria-modal="true" aria-label="Einlade-Regeln" className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4" data-testid="invite-rules-popup">
      <div className="w-full sm:max-w-lg max-h-[92dvh] overflow-y-auto bg-[#fff7f9] rounded-t-[30px] sm:rounded-[30px] shadow-2xl fade-rise">
        <div className="menu-hero text-white px-6 pt-7 pb-6 relative overflow-hidden">
          <span className="absolute -right-10 -top-12 w-44 h-44 rounded-full bg-white/10" aria-hidden />
          <p className="relative text-[10px] font-extrabold uppercase tracking-[0.25em] text-white/80">Neu in der Community</p>
          <h2 className="relative font-display text-2xl sm:text-3xl font-black leading-tight mt-1">Gemeinsam wachsen wir 💖</h2>
          <p className="relative text-sm text-white/90 mt-2">Unsere Community lebt von euch. Darum schaltest du Spiele und Prämien jetzt frei, indem du Freunde einlädst. Posten, liken und Punkte sammeln geht natürlich weiterhin ohne Einladung.</p>
        </div>
        <div className="p-5 sm:p-6 space-y-5">
          <ul className="space-y-2" data-testid="invite-rules-gates">
            {g.gates.map((x) => (
              <li key={x.id} className={`flex items-center gap-3 rounded-2xl px-4 py-3 border bg-white ${x.unlocked ? "border-emerald-200" : "border-pink-100"}`}>
                <span className={`w-9 h-9 rounded-xl flex items-center justify-center ${x.unlocked ? "bg-emerald-500" : "bg-rose-500"} text-white`}>{x.unlocked ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}</span>
                <span className="flex-1 min-w-0 font-semibold text-slate-800 text-sm">{x.label}</span>
                <span className={`text-xs font-extrabold ${x.unlocked ? "text-emerald-600" : "text-rose-600"}`}>{x.unlocked ? "Frei ✓" : `ab ${x.need} ${x.need === 1 ? "Freund" : "Freunden"}`}</span>
              </li>
            ))}
          </ul>
          <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3.5 flex items-start gap-3 text-sm text-amber-900">
            <Crown className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" fill="currentColor" />
            <span>Extra: Jeden Monat bekommen die <b>Top 3 Werber</b> +500, +250 und +100 Punkte – und Platz 1 trägt einen Monat lang die Krone im Profil.</span>
          </div>
          <div>
            <p className="font-bold text-slate-800 mb-2">So viele Wege, Freunde einzuladen:</p>
            <InviteWays compact onPick={onClose} />
          </div>
          <div className="space-y-2 pt-1">
            <button onClick={invite} data-testid="invite-rules-invite" className="w-full h-12 rounded-full bg-rose-600 text-white font-extrabold inline-flex items-center justify-center gap-2 shadow-lg shadow-rose-300 active:scale-[0.98] transition-transform"><Share2 className="w-5 h-5" /> Jetzt Freunde einladen</button>
            <button onClick={() => { onClose(); navigate("/einladen"); }} data-testid="invite-rules-more" className="w-full h-11 rounded-full bg-white border border-pink-200 text-rose-600 font-bold text-sm">Alle Einlade-Vorteile ansehen</button>
            <button onClick={onClose} data-testid="invite-rules-later" className="w-full h-9 text-slate-500 text-sm font-semibold">Später</button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function InviteRulesPopup() {
  const { user } = useAuth();
  const g = useGrowth();
  const [open, setOpen] = useState(false);
  const due = Boolean(user && g && !g.rules_seen && g.gates);
  useEffect(() => {
    if (!due) return;
    whenFree("invite-rules", () => setOpen(true));
  }, [due]);
  if (!open || !g) return null;
  const close = () => { setOpen(false); api.post("/growth/rules-seen").then(loadGrowth).catch(() => {}); };
  return <Sheet g={g} onClose={close} />;
}
